import {
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Iconify from '@/components/Iconify';
import RightSidebarTitle from '@/features/app/components/right-sidebar/right-sidebar-partials/RightSidebarTitle';
import useAuth from '@/hooks/useAuth';
import {
  archiveAiMonitorConversation,
  askAiMonitor,
  getAiMonitorConversationMessages,
  getAiMonitorConversations,
} from '../api';
import AiMonitorMessageContent, {
  hasAiMonitorStructuredContent,
} from './AiMonitorMessageContent';
import type {
  AiMonitorConversation,
  AiMonitorMessageRole,
  AiMonitorPersistedMessage,
  AiMonitorRange,
  AiMonitorSource,
  AskAiMonitorInput,
} from '../types';

const MESSAGE_MAX_LENGTH = 2000;
const COOLDOWN_MS = 60 * 1000;
const AI_MONITOR_CONVERSATIONS_QUERY_KEY = ['aiMonitor', 'conversations'];

type AiMonitorMessage = {
  id: string;
  persistedId?: number;
  role: AiMonitorMessageRole;
  content: string;
  sources?: AiMonitorSource[];
  suggestedFollowups?: string[];
  createdAt?: string;
};

type ErrorState = {
  message: string;
  severity: 'error' | 'warning' | 'info';
  disableInput?: boolean;
};

type StarterPrompt = {
  label: string;
  prompt: string;
  icon: string;
};

type StarterPromptGroup = {
  title: string;
  prompts: StarterPrompt[];
};

const RANGE_OPTIONS: { value: AiMonitorRange; label: string }[] = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: 'ytd', label: 'YTD' },
];
const EMPTY_STATE_TITLE = 'What should we check first?';

const STARTER_PROMPT_GROUPS: StarterPromptGroup[] = [
  {
    title: 'Today',
    prompts: [
      {
        label: 'Today at risk',
        prompt:
          'Create a concise operations brief for today. Include counts for total sessions, unassigned sessions, cancelled sessions, failed or unpaid payments, urgent and high-priority tasks, and the top issues to handle first. For each issue include severity, source link, owner if available, and next step.',
        icon: 'eva:alert-triangle-outline',
      },
      {
        label: 'Manager handoff',
        prompt:
          'Write a short manager handoff for the selected range. Summarize the most important sessions, cleaners, payments, clients, and tasks that need manual follow-up. Use bullets with owner, deadline, source link, and next action.',
        icon: 'eva:file-text-outline',
      },
    ],
  },
  {
    title: 'Schedule',
    prompts: [
      {
        label: 'Tomorrow cover',
        prompt:
          'How many booking sessions are scheduled tomorrow? Break them down by assigned vs unassigned, service type, cleaner, and status. Flag sessions with no cleaner, payment issues, client notes, or open urgent tasks. Include source links.',
        icon: 'eva:calendar-outline',
      },
      {
        label: 'Unassigned next 48h',
        prompt:
          'Show booking sessions in the next 48 hours that are unassigned or need manual scheduling attention. Return session id, date, time, booking id, client, address, service, status, source link, and suggested assignment action.',
        icon: 'eva:person-delete-outline',
      },
    ],
  },
  {
    title: 'Payments',
    prompts: [
      {
        label: 'Failed payments',
        prompt:
          'Show failed or unpaid payments in the selected range with client, amount, booking, booking session id/link, failure reason, payment status, attempt date, and next collection step. Link each transaction to its booking session where possible.',
        icon: 'eva:credit-card-outline',
      },
      {
        label: 'Payment due sessions',
        prompt:
          'List upcoming sessions in the selected range where payment is due, unpaid, failed, or needs checking before the job. Include session id, client, amount if available, booking id, date/time, cleaner if assigned, source link, and recommended follow-up.',
        icon: 'eva:clock-outline',
      },
    ],
  },
  {
    title: 'Tasks',
    prompts: [
      {
        label: 'Urgent tasks',
        prompt:
          'Show open urgent and high priority tasks with task ID, title/location, priority, assignee, due date, section, tags, source link, and the next action needed.',
        icon: 'eva:checkmark-square-2-outline',
      },
      {
        label: 'Overdue by owner',
        prompt:
          'Show overdue open tasks grouped by assignee. Prioritize urgent and high-priority items, include task ID, title/location, due date, section, tags, source link, and the next action each owner should take.',
        icon: 'eva:people-outline',
      },
    ],
  },
  {
    title: 'Clients',
    prompts: [
      {
        label: 'Client risk list',
        prompt:
          'Which clients need attention first in the selected range? Rank them by operational risk using failed payments, upcoming sessions, cancellations, notes, and open tasks. Include source links and next action.',
        icon: 'eva:person-outline',
      },
      {
        label: 'Issue timeline',
        prompt:
          'Create a timeline for the booking or client with the most recent operational issue. Include sessions, payments, notes, tasks, status changes, source links, and what should happen next.',
        icon: 'eva:clock-outline',
      },
    ],
  },
  {
    title: 'Audit',
    prompts: [
      {
        label: 'Evidence pack',
        prompt:
          'Show the evidence behind the current operational risks as a source panel grouped by bookings, sessions, payments, clients, and tasks. Keep it concise and include only links that support the answer.',
        icon: 'eva:link-2-outline',
      },
      {
        label: 'Data gaps',
        prompt:
          'Identify missing or incomplete operational data in the selected range, such as sessions without cleaners, payments without linked sessions, tasks missing assignees or due dates, and records without useful source links. Include recommended cleanup actions.',
        icon: 'eva:question-mark-circle-outline',
      },
    ],
  },
];

const aiMonitorConversationMessagesQueryKey = (
  conversationId?: number | null
) => ['aiMonitor', 'conversationMessages', conversationId];

const getErrorStatus = (error: unknown) => {
  if (error && typeof error === 'object') {
    const candidate = error as {
      status?: unknown;
      statusCode?: unknown;
      response?: { status?: unknown; data?: { status?: unknown } };
    };

    const statuses = [
      candidate.status,
      candidate.statusCode,
      candidate.response?.status,
      candidate.response?.data?.status,
    ];
    const status = statuses.find((value) => typeof value === 'number');

    return typeof status === 'number' ? status : null;
  }

  return null;
};

const getErrorMessageValue = (error: unknown) => {
  if (typeof error === 'string') return error;

  if (error && typeof error === 'object') {
    const candidate = error as {
      error?: unknown;
      message?: unknown;
      response?: { data?: { message?: unknown } };
    };

    const message =
      candidate.message ?? candidate.response?.data?.message ?? candidate.error;

    return typeof message === 'string' ? message : '';
  }

  return '';
};

const isConversationNotFoundError = (error: unknown) =>
  getErrorStatus(error) === 404 ||
  getErrorMessageValue(error) === 'aiMonitorConversationNotFound';

const getAiMonitorErrorState = (error: unknown): ErrorState => {
  const status = getErrorStatus(error);
  const message = getErrorMessageValue(error);

  if (
    status === 403 ||
    message === 'Access Restricted' ||
    message === 'Admin access required'
  ) {
    return {
      message:
        'Access restricted. AI Monitor is available to superadmins only.',
      severity: 'warning',
      disableInput: true,
    };
  }

  if (status === 429 || message === 'aiMonitorRateLimitExceeded') {
    return {
      message: 'Rate limit reached. Try again in about one minute.',
      severity: 'warning',
    };
  }

  if (
    status === 502 ||
    message === 'aiMonitorUnavailable' ||
    message === 'aiMonitorToolLimitExceeded'
  ) {
    return {
      message: 'AI Monitor is temporarily unavailable. Please retry.',
      severity: 'error',
    };
  }

  if (status === 503 || message === 'openaiNotConfigured') {
    return {
      message: 'AI Monitor is not configured on the backend.',
      severity: 'error',
      disableInput: true,
    };
  }

  if (status === 401) {
    return {
      message:
        'Your session could not be verified. Sign in again to use AI Monitor.',
      severity: 'error',
      disableInput: true,
    };
  }

  if (status === 400) {
    return {
      message:
        message || 'Check the message and selected range, then try again.',
      severity: 'warning',
    };
  }

  return {
    message: 'AI Monitor could not answer right now. Please retry.',
    severity: 'error',
  };
};

const getSourceKey = (source: AiMonitorSource, index: number) =>
  `${source.type}-${source.id}-${index}`;

const getTimestamp = (value?: string | null) => {
  if (!value) return 0;

  const timestamp = new Date(value).getTime();

  return Number.isFinite(timestamp) ? timestamp : 0;
};

const sortConversations = (conversations: AiMonitorConversation[] = []) =>
  [...conversations].sort((firstConversation, secondConversation) => {
    const timestampDifference =
      getTimestamp(secondConversation.updated_at) -
      getTimestamp(firstConversation.updated_at);

    return timestampDifference || secondConversation.id - firstConversation.id;
  });

const sortPersistedMessages = (messages: AiMonitorPersistedMessage[] = []) =>
  [...messages].sort((firstMessage, secondMessage) => {
    const timestampDifference =
      getTimestamp(firstMessage.created_at) -
      getTimestamp(secondMessage.created_at);

    return timestampDifference || firstMessage.id - secondMessage.id;
  });

const getSourceList = (sources: unknown): AiMonitorSource[] =>
  Array.isArray(sources)
    ? sources.filter(
        (source): source is AiMonitorSource =>
          source !== null &&
          typeof source === 'object' &&
          typeof (source as AiMonitorSource).label === 'string'
      )
    : [];

const getStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];

const toUiMessage = (message: AiMonitorPersistedMessage): AiMonitorMessage => ({
  id: `ai-monitor-persisted-message-${message.id}`,
  persistedId: message.id,
  role: message.role,
  content: message.content,
  sources: getSourceList(message.sources),
  suggestedFollowups: getStringList(message.suggested_followups),
  createdAt: message.created_at,
});

const getConversationTitle = (conversation: AiMonitorConversation) =>
  conversation.title?.trim() || 'Untitled chat';

const formatConversationDate = (conversation: AiMonitorConversation) => {
  const timestamp = getTimestamp(conversation.updated_at);

  if (!timestamp) return '';

  return new Date(timestamp).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });
};

export default function AiMonitorBar() {
  const { isSuperAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [activeConversationId, setActiveConversationId] = useState<
    number | null
  >(null);
  const [messagesQueryConversationId, setMessagesQueryConversationId] =
    useState<number | null>(null);
  const [messages, setMessages] = useState<AiMonitorMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [selectedRange, setSelectedRange] = useState<AiMonitorRange>('30d');
  const [errorState, setErrorState] = useState<ErrorState | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [, setClock] = useState(0);
  const messageIdRef = useRef(0);
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);

  const handleConversationNotFound = useCallback(() => {
    setActiveConversationId(null);
    setMessagesQueryConversationId(null);
    setMessages([]);
    setErrorState({
      message: 'Conversation no longer available.',
      severity: 'info',
    });
    queryClient.invalidateQueries(AI_MONITOR_CONVERSATIONS_QUERY_KEY);
  }, [queryClient]);

  const conversationsQuery = useQuery({
    enabled: isSuperAdmin,
    queryFn: () => getAiMonitorConversations(),
    queryKey: AI_MONITOR_CONVERSATIONS_QUERY_KEY,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    onError: (error) => {
      setErrorState(getAiMonitorErrorState(error));
    },
  });

  const conversationMessagesQuery = useQuery({
    enabled: isSuperAdmin && messagesQueryConversationId !== null,
    queryFn: () =>
      getAiMonitorConversationMessages(messagesQueryConversationId as number),
    queryKey: aiMonitorConversationMessagesQueryKey(
      messagesQueryConversationId
    ),
    refetchOnWindowFocus: false,
    onSuccess: (persistedMessages) => {
      setMessages(sortPersistedMessages(persistedMessages).map(toUiMessage));
      setErrorState(null);
    },
    onError: (error) => {
      if (isConversationNotFoundError(error)) {
        handleConversationNotFound();
        return;
      }

      setErrorState(getAiMonitorErrorState(error));
    },
  });

  const askMutation = useMutation({
    mutationFn: askAiMonitor,
  });

  const archiveMutation = useMutation({
    mutationFn: archiveAiMonitorConversation,
    onSuccess: (_data, conversationId) => {
      queryClient.setQueryData<AiMonitorConversation[]>(
        AI_MONITOR_CONVERSATIONS_QUERY_KEY,
        (previousConversations = []) =>
          previousConversations.filter(
            (conversation) => conversation.id !== conversationId
          )
      );
      queryClient.removeQueries(
        aiMonitorConversationMessagesQueryKey(conversationId)
      );

      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
        setMessagesQueryConversationId(null);
        setMessages([]);
        setDraft('');
        setErrorState(null);
      }
    },
    onError: (error) => {
      setErrorState(getAiMonitorErrorState(error));
    },
  });

  const nextMessageId = useCallback(() => {
    messageIdRef.current += 1;
    return `ai-monitor-message-${messageIdRef.current}`;
  }, []);

  const conversations = useMemo(
    () => sortConversations(conversationsQuery.data ?? []),
    [conversationsQuery.data]
  );

  const isCoolingDown = Boolean(
    cooldownUntil && cooldownUntil > new Date().getTime()
  );
  const inputDisabled =
    !isSuperAdmin ||
    askMutation.isLoading ||
    conversationMessagesQuery.isFetching ||
    isCoolingDown ||
    errorState?.disableInput === true;
  const historyControlsDisabled =
    askMutation.isLoading ||
    archiveMutation.isLoading ||
    conversationMessagesQuery.isFetching;
  const isEmptyNewChat =
    messages.length === 0 &&
    activeConversationId === null &&
    !conversationMessagesQuery.isFetching;

  useEffect(() => {
    if (!cooldownUntil) return undefined;

    const intervalId = window.setInterval(() => {
      setClock((value) => value + 1);

      if (cooldownUntil <= new Date().getTime()) {
        setCooldownUntil(null);
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [cooldownUntil]);

  useEffect(() => {
    const scrollAnchor = scrollAnchorRef.current;

    if (typeof scrollAnchor?.scrollIntoView === 'function') {
      scrollAnchor.scrollIntoView({ block: 'end' });
    }
  }, [messages, askMutation.isLoading, conversationMessagesQuery.isFetching]);

  const handleNewChat = useCallback(() => {
    if (historyControlsDisabled) return;

    setActiveConversationId(null);
    setMessagesQueryConversationId(null);
    setMessages([]);
    setDraft('');
    setErrorState(null);
  }, [historyControlsDisabled]);

  const handleSelectConversation = useCallback(
    (conversationId: number) => {
      if (historyControlsDisabled || activeConversationId === conversationId) {
        return;
      }

      setActiveConversationId(conversationId);
      setMessagesQueryConversationId(conversationId);
      setMessages([]);
      setDraft('');
      setErrorState(null);
    },
    [activeConversationId, historyControlsDisabled]
  );

  const handleArchiveConversation = useCallback(
    (
      event: MouseEvent<HTMLButtonElement>,
      conversation: AiMonitorConversation
    ) => {
      event.stopPropagation();
      void archiveMutation.mutateAsync(conversation.id);
    },
    [archiveMutation]
  );

  const submitMessage = useCallback(
    async (messageText: string) => {
      const trimmedMessage = messageText.trim();

      if (!trimmedMessage) {
        setErrorState({
          message: 'Enter a message for AI Monitor.',
          severity: 'warning',
        });
        return;
      }

      if (trimmedMessage.length > MESSAGE_MAX_LENGTH) {
        setErrorState({
          message: `Message must be ${MESSAGE_MAX_LENGTH} characters or fewer.`,
          severity: 'warning',
        });
        return;
      }

      if (inputDisabled) {
        return;
      }

      const userMessage: AiMonitorMessage = {
        id: nextMessageId(),
        role: 'user',
        content: trimmedMessage,
      };
      const payload: AskAiMonitorInput = {
        message: trimmedMessage,
        range: selectedRange,
      };

      if (activeConversationId !== null) {
        payload.conversation_id = activeConversationId;
      }

      setMessages((currentMessages) => [...currentMessages, userMessage]);
      setDraft('');
      setErrorState(null);

      try {
        const response = await askMutation.mutateAsync(payload);
        const answer = response.data;

        setActiveConversationId(answer.conversation_id);
        setMessagesQueryConversationId(null);
        setMessages((currentMessages) => [
          ...currentMessages,
          {
            id: `ai-monitor-persisted-message-${answer.message_id}`,
            persistedId: answer.message_id,
            role: 'assistant',
            content: answer.answer,
            sources: getSourceList(answer.sources),
            suggestedFollowups: getStringList(answer.suggested_followups),
          },
        ]);
        queryClient.invalidateQueries(AI_MONITOR_CONVERSATIONS_QUERY_KEY);
      } catch (error) {
        if (isConversationNotFoundError(error)) {
          handleConversationNotFound();
          setDraft(trimmedMessage);
          return;
        }

        const nextErrorState = getAiMonitorErrorState(error);

        setErrorState(nextErrorState);
        setDraft(trimmedMessage);

        if (
          getErrorStatus(error) === 429 ||
          getErrorMessageValue(error) === 'aiMonitorRateLimitExceeded'
        ) {
          setCooldownUntil(new Date().getTime() + COOLDOWN_MS);
        }
      }
    },
    [
      activeConversationId,
      askMutation,
      handleConversationNotFound,
      inputDisabled,
      nextMessageId,
      queryClient,
      selectedRange,
    ]
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submitMessage(draft);
  };

  const handleDraftKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submitMessage(draft);
    }
  };

  const renderComposer = (placement: 'hero' | 'dock') => {
    const isHero = placement === 'hero';

    return (
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          maxWidth: isHero ? 940 : 'none',
          mx: isHero ? 'auto' : 0,
          p: isHero ? 0 : 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            minHeight: 58,
            px: 1,
            py: 0.75,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 999,
            bgcolor: 'background.paper',
            boxShadow: isHero
              ? '0 14px 40px rgba(16, 24, 40, 0.08)'
              : '0 4px 14px rgba(16, 24, 40, 0.06)',
          }}
        >
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={4}
            variant="standard"
            placeholder="Ask anything"
            value={draft}
            disabled={inputDisabled}
            inputProps={{
              'aria-label': 'Message',
              maxLength: MESSAGE_MAX_LENGTH + 1,
            }}
            InputProps={{ disableUnderline: true }}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleDraftKeyDown}
            sx={{
              minWidth: 0,
              '& .MuiInputBase-root': {
                alignItems: 'center',
                fontSize: isHero ? 18 : 16,
                lineHeight: 1.35,
              },
              '& textarea::placeholder': {
                color: 'text.secondary',
                opacity: 0.85,
              },
            }}
          />

          <TextField
            select
            variant="standard"
            value={selectedRange}
            disabled={!isSuperAdmin || askMutation.isLoading}
            SelectProps={{ native: true }}
            InputProps={{ disableUnderline: true }}
            inputProps={{ 'aria-label': 'Date range' }}
            onChange={(event) =>
              setSelectedRange(event.target.value as AiMonitorRange)
            }
            sx={{
              width: 74,
              flexShrink: 0,
              '& .MuiInputBase-root': {
                fontSize: isHero ? 17 : 15,
                fontWeight: 700,
                color: 'text.secondary',
              },
              '& select': {
                py: 0,
                pr: '22px !important',
              },
            }}
          >
            {RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>

          <Tooltip title="Send">
            <span>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={askMutation.isLoading}
                disabled={inputDisabled || !draft.trim()}
                sx={{
                  minWidth: 46,
                  width: 46,
                  height: 46,
                  borderRadius: '50%',
                  flexShrink: 0,
                  p: 0,
                  bgcolor: 'common.black',
                  color: 'common.white',
                  '&:hover': {
                    bgcolor: 'grey.900',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
                aria-label="Send AI Monitor message"
              >
                <Iconify icon="eva:paper-plane-fill" width={20} />
              </LoadingButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    );
  };

  const renderStarterPrompts = () => (
    <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto' }}>
      <Stack spacing={1.25}>
        <Typography
          variant="caption"
          align="center"
          sx={{
            color: 'text.secondary',
            fontWeight: 800,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
          }}
        >
          Suggested checks
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          {STARTER_PROMPT_GROUPS.map((group) => (
            <Box
              key={group.title}
              sx={{
                minWidth: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50',
                px: 1.25,
                py: 1,
              }}
            >
              <Stack spacing={0.85}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 800,
                    letterSpacing: 0.4,
                    lineHeight: 1.2,
                    textTransform: 'uppercase',
                  }}
                >
                  {group.title}
                </Typography>

                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  {group.prompts.map((starterPrompt) => (
                    <Tooltip
                      key={starterPrompt.label}
                      title={starterPrompt.prompt}
                    >
                      <span>
                        <Chip
                          label={starterPrompt.label}
                          icon={
                            <Iconify icon={starterPrompt.icon} width={16} />
                          }
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            void submitMessage(starterPrompt.prompt)
                          }
                          disabled={inputDisabled}
                          sx={{
                            borderRadius: 0.75,
                            bgcolor: 'background.paper',
                            borderColor: 'divider',
                            color: 'text.primary',
                            fontWeight: 700,
                            '& .MuiChip-icon': {
                              color: 'text.secondary',
                            },
                            '&:hover': {
                              bgcolor: 'grey.100',
                            },
                          }}
                        />
                      </span>
                    </Tooltip>
                  ))}
                </Stack>
              </Stack>
            </Box>
          ))}
        </Box>
      </Stack>
    </Box>
  );

  return (
    <Paper
      square
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      <RightSidebarTitle title="AI Monitor" />

      <Box sx={{ display: 'flex', flex: '1 1 auto', minHeight: 0 }}>
        <Box
          sx={{
            width: 172,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Box
            sx={{ p: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Button
              fullWidth
              size="small"
              variant={activeConversationId === null ? 'contained' : 'outlined'}
              startIcon={<Iconify icon="eva:plus-fill" width={16} />}
              onClick={handleNewChat}
              disabled={historyControlsDisabled}
              sx={{ borderRadius: 1, justifyContent: 'flex-start' }}
            >
              New chat
            </Button>
          </Box>

          <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', p: 1 }}>
            <Stack spacing={0.75}>
              <Typography
                variant="caption"
                sx={{ px: 0.5, color: 'text.secondary', fontWeight: 700 }}
              >
                History
              </Typography>

              {conversationsQuery.isLoading && (
                <Stack alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={18} />
                </Stack>
              )}

              {!conversationsQuery.isLoading && conversations.length === 0 && (
                <Typography
                  variant="caption"
                  sx={{ px: 0.5, color: 'text.secondary' }}
                >
                  No chats yet
                </Typography>
              )}

              {conversations.map((conversation) => {
                const title = getConversationTitle(conversation);
                const isActive = activeConversationId === conversation.id;
                const isArchiving =
                  archiveMutation.isLoading &&
                  archiveMutation.variables === conversation.id;

                return (
                  <Stack
                    key={conversation.id}
                    direction="row"
                    alignItems="center"
                    spacing={0.25}
                    sx={{
                      borderRadius: 1,
                      bgcolor: isActive ? 'background.paper' : 'transparent',
                      border: '1px solid',
                      borderColor: isActive ? 'divider' : 'transparent',
                      boxShadow: isActive
                        ? '0 1px 3px rgba(16, 24, 40, 0.08)'
                        : 'none',
                      '&:hover': {
                        bgcolor: 'background.paper',
                      },
                    }}
                  >
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Open AI Monitor conversation ${title}`}
                      onClick={() => handleSelectConversation(conversation.id)}
                      disabled={historyControlsDisabled}
                      sx={{
                        all: 'unset',
                        boxSizing: 'border-box',
                        cursor: historyControlsDisabled ? 'default' : 'pointer',
                        minWidth: 0,
                        flex: '1 1 auto',
                        px: 1,
                        py: 0.75,
                      }}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ fontWeight: isActive ? 800 : 600 }}
                      >
                        {title}
                      </Typography>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ display: 'block', color: 'text.secondary' }}
                      >
                        {formatConversationDate(conversation)}
                      </Typography>
                    </Box>

                    <Tooltip title="Archive">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Archive ${title}`}
                          onClick={(event) =>
                            handleArchiveConversation(event, conversation)
                          }
                          disabled={historyControlsDisabled || isArchiving}
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: 1,
                            mr: 0.25,
                          }}
                        >
                          {isArchiving ? (
                            <CircularProgress size={14} />
                          ) : (
                            <Iconify icon="eva:archive-outline" width={16} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack
            spacing={1.5}
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Alert severity="info" sx={{ py: 0.5 }}>
              Read-only operational answers. Changes must be made manually.
            </Alert>
          </Stack>

          <Box
            sx={{
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              px: 2,
              py: 2,
            }}
          >
            <Stack
              spacing={1.5}
              sx={{ minHeight: isEmptyNewChat ? '100%' : undefined }}
            >
              {conversationMessagesQuery.isFetching &&
                activeConversationId !== null && (
                  <Stack direction="row" gap={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Loading conversation...
                    </Typography>
                  </Stack>
                )}

              {isEmptyNewChat && (
                <Stack
                  spacing={2.5}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    minHeight: '100%',
                    px: { xs: 0, sm: 2 },
                    py: 4,
                  }}
                >
                  <Typography
                    variant="h4"
                    align="center"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 700,
                      letterSpacing: 0,
                    }}
                  >
                    {EMPTY_STATE_TITLE}
                  </Typography>

                  {renderComposer('hero')}

                  {renderStarterPrompts()}
                </Stack>
              )}

              {messages.map((message) => {
                const isUserMessage = message.role === 'user';
                const linkedSources =
                  message.sources?.filter((source) => Boolean(source.url)) ??
                  [];
                const hasStructuredContent =
                  !isUserMessage &&
                  hasAiMonitorStructuredContent(message.content);

                return (
                  <Stack
                    key={message.id}
                    alignItems={isUserMessage ? 'flex-end' : 'flex-start'}
                    spacing={0.75}
                  >
                    <Box
                      sx={{
                        maxWidth: '92%',
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 1,
                        bgcolor: isUserMessage ? 'primary.main' : 'grey.100',
                        color: isUserMessage
                          ? 'primary.contrastText'
                          : 'text.primary',
                      }}
                    >
                      {isUserMessage ? (
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {message.content}
                        </Typography>
                      ) : (
                        <AiMonitorMessageContent
                          content={message.content}
                          sources={message.sources}
                          inputDisabled={inputDisabled}
                          onSubmitPrompt={(prompt) =>
                            void submitMessage(prompt)
                          }
                        />
                      )}
                    </Box>

                    {!isUserMessage &&
                      !hasStructuredContent &&
                      linkedSources.length > 0 && (
                        <Stack
                          direction="row"
                          gap={0.75}
                          flexWrap="wrap"
                          sx={{ maxWidth: '92%', minWidth: 0 }}
                        >
                          {linkedSources.map((source, index) => (
                            <Link
                              key={getSourceKey(source, index)}
                              href={source.url ?? undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="caption"
                              sx={{
                                px: 1,
                                py: 0.35,
                                borderRadius: 0.75,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                textDecoration: 'none',
                                fontWeight: 700,
                              }}
                            >
                              {source.label}
                            </Link>
                          ))}
                        </Stack>
                      )}

                    {!isUserMessage &&
                      Boolean(message.suggestedFollowups?.length) && (
                        <Stack
                          direction="row"
                          gap={0.75}
                          flexWrap="wrap"
                          sx={{ maxWidth: '92%', minWidth: 0 }}
                        >
                          {message.suggestedFollowups?.map((followup) => (
                            <Chip
                              key={followup}
                              label={followup}
                              size="small"
                              color="primary"
                              variant="outlined"
                              onClick={() => void submitMessage(followup)}
                              disabled={inputDisabled}
                              sx={{
                                borderRadius: 1,
                                height: 'auto',
                                maxWidth: '100%',
                                minHeight: 28,
                                '& .MuiChip-label': {
                                  display: 'block',
                                  overflowWrap: 'anywhere',
                                  py: 0.5,
                                  whiteSpace: 'normal',
                                },
                              }}
                            />
                          ))}
                        </Stack>
                      )}
                  </Stack>
                );
              })}

              {askMutation.isLoading && (
                <Box
                  sx={{
                    alignSelf: 'flex-start',
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 1,
                    bgcolor: 'grey.100',
                  }}
                >
                  <Typography variant="body2">
                    Checking system data...
                  </Typography>
                </Box>
              )}

              <div ref={scrollAnchorRef} />
            </Stack>
          </Box>

          {errorState && (
            <Box sx={{ px: 2, pb: 1 }}>
              <Alert severity={errorState.severity} sx={{ py: 0.5 }}>
                {errorState.message}
              </Alert>
            </Box>
          )}

          {!isEmptyNewChat && (
            <>
              <Divider />
              {renderComposer('dock')}
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
